<script lang="ts">
  import { _ } from 'svelte-i18n';

  import { languages, locale, setLocale } from '$lib/i18n';

  /**
   * The language control in the mobile drawer.
   *
   * On desktop the same choice lives in the account menu, which has room for
   * the language's own name rather than a two-letter code. Here it stays a
   * `select`: the drawer is a column of controls and a native one is what a
   * phone opens as a picker.
   *
   * The list itself is in `$lib/i18n`, shared with that menu — including the
   * reason there are no flags on it.
   */

  function handleLanguageChange(event: Event & { currentTarget: HTMLSelectElement }) {
    setLocale((event.currentTarget as HTMLSelectElement).value);
  }

  /**
   * Whether this sits on the navy top bar.
   *
   * It has to be told, because it renders on three grounds and only one of
   * them is navy: the signed-out top bar, the mobile drawer's chrome tint, and
   * the login screen's header. It was unconditionally `.chrome-control` —
   * white text on a tenth of white — which is right on navy and invisible on
   * the other two. Both were shipped that way.
   *
   * Default light, because two of the three are, and because a control that
   * disappears is a worse failure than one that is merely plain.
   */
  export let onNavy = false;
</script>

<div class="language-switcher">
  <!--
    `color-scheme: dark` is not decoration on navy. The control is a native
    `<select>`, and the list it drops is drawn by the operating system rather
    than by us: styling the closed control white-on-navy without it leaves the
    open list white-on-white on every platform that honours the page's scheme.
    It is the one property that reaches inside it — and it is exactly as wrong
    on a light ground, which is why it is conditional too.
  -->
  <select
    value={$locale}
    on:change={handleLanguageChange}
    style={onNavy ? 'color-scheme: dark' : ''}
    class="min-w-[4.5rem] rounded-control py-1.5 pl-2.5 pr-7 text-xs font-medium focus:outline-none sm:min-w-[5rem] sm:pl-3 sm:pr-8 sm:text-sm {onNavy
      ? 'chrome-control'
      : 'border border-hairline bg-surface-panel text-gray-700 focus:border-accent-400'}"
    aria-label={$_('a11y.selectLanguage')}
  >
    {#each languages as lang (lang.code)}
      <option value={lang.code}>{lang.short}</option>
    {/each}
  </select>
</div>

<style>
  .language-switcher {
    position: relative;
    display: inline-block;
    flex-shrink: 0;
  }
</style>
